/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type ChangeEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { type WaitlistPriority } from '../../lib/api/waitlist.js';
import { useCreateWaitlistEntry } from '../../lib/queries/waitlist.js';

const PRIORITIES: readonly WaitlistPriority[] = ['low', 'medium', 'high'];
const INPUT =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL = 'mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300';

interface FormState {
  childName: string;
  age: string;
  concern: string;
  preferredSlots: string;
  priority: WaitlistPriority;
}

const EMPTY: FormState = {
  childName: '',
  age: '',
  concern: '',
  preferredSlots: '',
  priority: 'medium',
};

/** "Add to Waitlist" (Figma). */
export function AddWaitlistModal({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateWaitlistEntry();
  const [form, setForm] = useState<FormState>(EMPTY);

  const set = useCallback(
    (key: keyof FormState) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    },
    [],
  );
  const onModal = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
      }
    },
    [onClose],
  );
  const submit = useCallback(() => {
    if (form.childName.trim().length < 2) {
      return;
    }
    const ageNum = Number(form.age);
    create.mutate(
      {
        childName: form.childName.trim(),
        priority: form.priority,
        ...(form.age !== '' && Number.isFinite(ageNum) ? { age: ageNum } : {}),
        ...(form.concern !== '' ? { concern: form.concern.trim() } : {}),
        ...(form.preferredSlots !== '' ? { preferredSlots: form.preferredSlots.trim() } : {}),
      },
      {
        onSuccess: () => {
          setForm(EMPTY);
          onClose();
        },
      },
    );
  }, [create, form, onClose]);

  return (
    <Modal
      open={open}
      onOpenChange={onModal}
      title={t('waitlist.addTitle')}
      closeLabel={t('common.cancel')}
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="wl-name" className={LABEL}>
            {t('waitlist.childName')}
          </label>
          <input
            id="wl-name"
            className={INPUT}
            value={form.childName}
            onChange={set('childName')}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="wl-age" className={LABEL}>
              {t('waitlist.age')}
            </label>
            <input
              id="wl-age"
              type="number"
              min={0}
              max={25}
              className={INPUT}
              value={form.age}
              onChange={set('age')}
            />
          </div>
          <div>
            <label htmlFor="wl-prio" className={LABEL}>
              {t('waitlist.priority')}
            </label>
            <select id="wl-prio" className={INPUT} value={form.priority} onChange={set('priority')}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`waitlist.priorities.${p}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="wl-concern" className={LABEL}>
            {t('waitlist.concern')}
          </label>
          <input id="wl-concern" className={INPUT} value={form.concern} onChange={set('concern')} />
        </div>
        <div>
          <label htmlFor="wl-slots" className={LABEL}>
            {t('waitlist.preferredSlots')}
          </label>
          <input
            id="wl-slots"
            className={INPUT}
            value={form.preferredSlots}
            onChange={set('preferredSlots')}
          />
        </div>
        <div className="flex justify-end">
          <Button type="button" onClick={submit} disabled={create.isPending}>
            {t('waitlist.add')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
